import React, { useState } from 'react';
import { ClipboardListIcon, HomeIcon, PlusCircleIcon, QrCodeIcon, ScanLineIcon, TruckIcon, UserIcon, WarehouseIcon } from 'lucide-react';
import type { FieldRole } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { FIELD_USERS } from '../../data/users';
import { PhoneFrame } from '../../components/shared/PhoneFrame';
import { DemoSidePanel } from '../../components/mobile/DemoSidePanel';
import { BottomNav, type NavItem } from '../../components/mobile/BottomNav';
import { FieldLogin } from './FieldLogin';
import { FieldHome } from './FieldHome';
import { CustodyWizard } from './CustodyWizard';
import { ReceiptScreen } from './ReceiptScreen';
import { StickerScreen } from './StickerScreen';
import { PickupScreen } from './PickupScreen';
import { StationReceive } from './StationReceive';
import { ReleaseFlow } from './ReleaseFlow';
import { FieldRecords } from './FieldRecords';
import { FieldCaseView } from './FieldCaseView';
import { FieldProfile } from './FieldProfile';
const NAV: Record<FieldRole, NavItem[]> = {
  sergeant: [{
    id: 'home',
    label: 'হোম',
    icon: HomeIcon
  }, {
    id: 'wizard',
    label: 'নতুন হেফাজত',
    icon: PlusCircleIcon
  }, {
    id: 'records',
    label: 'রেকর্ড',
    icon: ClipboardListIcon
  }, {
    id: 'profile',
    label: 'প্রোফাইল',
    icon: UserIcon
  }],
  wrecker: [{
    id: 'home',
    label: 'হোম',
    icon: HomeIcon
  }, {
    id: 'wizard',
    label: 'নতুন PCR',
    icon: PlusCircleIcon
  }, {
    id: 'pickup',
    label: 'পিকআপ',
    icon: TruckIcon
  }, {
    id: 'records',
    label: 'রেকর্ড',
    icon: ClipboardListIcon
  }],
  receiving: [{
    id: 'home',
    label: 'হোম',
    icon: HomeIcon
  }, {
    id: 'receive',
    label: 'স্ক্যান',
    icon: ScanLineIcon
  }, {
    id: 'records',
    label: 'ইনভেন্টরি',
    icon: WarehouseIcon
  }, {
    id: 'profile',
    label: 'প্রোফাইল',
    icon: UserIcon
  }],
  release: [{
    id: 'home',
    label: 'হোম',
    icon: HomeIcon
  }, {
    id: 'release',
    label: 'QR যাচাই',
    icon: QrCodeIcon
  }, {
    id: 'records',
    label: 'ইনভেন্টরি',
    icon: WarehouseIcon
  }, {
    id: 'profile',
    label: 'প্রোফাইল',
    icon: UserIcon
  }]
};
const HINTS: Record<FieldRole | 'none', string[]> = {
  none: ['যেকোনো ডেমো অ্যাকাউন্টে লগইন করুন অথবা বাম পাশ থেকে রোল নির্বাচন করুন।', 'সকল কার্যক্রম ওয়েব পোর্টালের একই কেস রেকর্ড হালনাগাদ করে।'],
  sergeant: ['“নতুন হেফাজত নির্দেশনা” দিয়ে কেস শুরু করুন।', 'চলমান রেকার অনুরোধ ও সাম্প্রতিক PCR হোমে দেখা যাবে।'],
  wrecker: ['পরিস্থিতি ১: “নতুন PCR তৈরি করুন” → তিন ধাপ পূরণ → রসিদ → বারকোড প্রিন্ট → সংযুক্তি নিশ্চিত → পিকআপ।', '“নমুনা তথ্য পূরণ” বোতাম দ্রুত ডেমোতে সাহায্য করে।'],
  receiving: ['পরিস্থিতি ২: স্ক্যান → পরিবহনাধীন যানবাহনের বারকোড নির্বাচন → জোন/স্লট দিয়ে গ্রহণ নিশ্চিত।', 'বাতিলকৃত স্টিকার স্ক্যান করলে সিস্টেম প্রত্যাখ্যান করবে।'],
  release: ['পরিস্থিতি ৬: PCR-2026-00424-এর QR স্ক্যান করে সম্পূর্ণ হস্তান্তর দেখান।', 'পরিস্থিতি ৭: PCR-2026-00426-এর ব্যবহৃত QR স্ক্যান করলে অবমুক্তি বন্ধ হবে।']
};
export function FieldApp({
  onExit
}: {
  onExit: () => void;
}) {
  const [role, setRole] = useState<FieldRole | null>(null);
  const [screen, setScreen] = useState<FieldScreen>({
    name: 'home'
  });
  const switchRole = (r: string) => {
    setRole(r as FieldRole);
    setScreen({
      name: 'home'
    });
  };
  const side = <DemoSidePanel title="মাঠ পর্যায়ের কর্মকর্তা অ্যাপ" roles={(Object.keys(FIELD_USERS) as FieldRole[]).map(k => ({
    id: k,
    label: FIELD_USERS[k].title,
    name: FIELD_USERS[k].name
  }))} active={role} onChange={switchRole} hints={HINTS[role ?? 'none']} data-id="element-1293" data-mp="z94wz3h6qsq5" />;
  if (!role) {
    return <PhoneFrame onExit={onExit} side={side} data-id="element-1294" data-mp="fkdz7673aw62">
        <FieldLogin onLogin={switchRole} onExit={onExit} data-id="element-1295" data-mp="fjkndqt9xgh2" />
      </PhoneFrame>;
  }
  const user = FIELD_USERS[role];
  const go = (s: FieldScreen) => setScreen(s);
  const home = () => setScreen({
    name: 'home'
  });
  const navActive = ['home', 'wizard', 'pickup', 'receive', 'release', 'records', 'profile'].includes(screen.name) ? screen.name : '';
  let body: React.ReactNode;
  switch (screen.name) {
    case 'wizard':
      body = <CustodyWizard role={role} user={user} go={go} onBack={home} data-id="element-1296" data-mp="34tgdsajeg3dj" />;
      break;
    case 'receipt':
      body = <ReceiptScreen pcr={screen.pcr} user={user} go={go} data-id="element-1297" data-mp="2zud6skjuzn2x" />;
      break;
    case 'sticker':
      body = <StickerScreen pcr={screen.pcr} user={user} go={go} data-id="element-1298" data-mp="3emorzew13z2b" />;
      break;
    case 'pickup':
      body = <PickupScreen pcr={screen.pcr} user={user} go={go} data-id="element-1299" data-mp="39t2q8hrelfla" />;
      break;
    case 'receive':
      body = <StationReceive user={user} go={go} data-id="element-1300" data-mp="3nkr0sgm07w05" />;
      break;
    case 'release':
      body = <ReleaseFlow user={user} go={go} data-id="element-1301" data-mp="3inh4ch076k2t" />;
      break;
    case 'records':
      body = <FieldRecords role={role} go={go} data-id="element-1302" data-mp="3ipbmjnk33y37" />;
      break;
    case 'case':
      body = <FieldCaseView pcr={screen.pcr} role={role} go={go} data-id="element-1303" data-mp="1ebzg7ins0ik" />;
      break;
    case 'profile':
      body = <FieldProfile user={user} onLogout={() => setRole(null)} onBack={home} data-id="element-1304" data-mp="3smgbtw9889o8" />;
      break;
    default:
      body = <FieldHome role={role} user={user} go={go} data-id="element-1305" data-mp="237066quih633" />;
  }
  return <PhoneFrame onExit={onExit} side={side} data-id="element-1306" data-mp="1y7nhs1nzrqye">
      <div className="flex min-h-0 flex-1 flex-col" data-id="element-1307" data-mp="1y8ymc3p9h2qk">{body}</div>
      <BottomNav items={NAV[role]} active={navActive} onChange={id => go({
      name: id
    } as FieldScreen)} data-id="element-1308" data-mp="dotimxwepmlb" />
    </PhoneFrame>;
}